import { IconDeviceMobile, IconExternalLink } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { BackLink } from "@/components/back-link";
import { ButtonLink } from "@/components/button-link";
import { DetailFacts, DetailLayout, DetailSection } from "@/components/detail-layout";
import { PageHeader } from "@/components/page-header";
import { orpc } from "@/lib/orpc";
import { ArchivedProductNotice } from "./-components/archived-product-notice";
import { ComposedBundleNotice } from "./-components/composed-bundle-notice";
import { LevelsTable } from "./-components/levels-table";
import { MovementForm } from "./-components/movement-form";
import { MovementHistory } from "./-components/movement-history";
import { HistorySearch, LIST_DEFAULTS } from "./-components/search";
import { StockLevelBadge } from "./-components/stock-level";

const variantQuery = (variantId: string) => orpc.stock.getVariant.queryOptions({ input: { variantId } });
const historyQuery = (variantId: string, search: HistorySearch) =>
	orpc.stock.listMovements.queryOptions({ input: { variantId, ...search } });

/**
 * One variant's stock (#62, #66): the stock per location, booking a movement (receipt, correction,
 * transfer to fulfilment), and the history with who, when, why and from / to where. The product
 * editor's side panel links here.
 */
export const Route = createFileRoute("/_admin/stock/$variantId")({
	validateSearch: HistorySearch,
	search: { middlewares: [stripSearchParams(LIST_DEFAULTS)] },
	loaderDeps: ({ search }) => search,
	loader: ({ context, params, deps }) =>
		Promise.all([
			context.queryClient.ensureQueryData(variantQuery(params.variantId)),
			context.queryClient.ensureQueryData(historyQuery(params.variantId, deps)),
		]),
	component: VariantStockPage,
});

function VariantStockPage() {
	const { t } = useTranslation("stock");
	const { variantId } = Route.useParams();
	const search = Route.useSearch();
	const { data: stock } = useSuspenseQuery(variantQuery(variantId));
	const { data: history } = useSuspenseQuery(historyQuery(variantId, search));
	const named = stock.variantName !== stock.productName;

	return (
		<DetailLayout
			header={
				<PageHeader
					back={<BackLink to="/stock">{t("title")}</BackLink>}
					title={named ? `${stock.productName} – ${stock.variantName}` : stock.productName}
					description={stock.sku}
					help="stock:variant"
					actions={
						<>
							<ButtonLink to="/product/$productId" params={{ productId: stock.productId }} variant="outline">
								<IconExternalLink aria-hidden />
								{t("variant.product")}
							</ButtonLink>
							<ButtonLink to="/stock/quick" search={{ variantId: stock.variantId }} variant="outline">
								<IconDeviceMobile aria-hidden />
								{t("quickLink")}
							</ButtonLink>
						</>
					}
				/>
			}
			aside={
				<>
					<DetailSection title={t("book.title")} description={t("book.description")} help="stock:book">
						{match(stock)
							.with({ archived: true }, () => <ArchivedProductNotice stock={stock} />)
							.with({ composedBundle: true }, () => <ComposedBundleNotice stock={stock} />)
							// Remounted per variant, so its defaults follow the variant shown.
							.otherwise(() => (
								<MovementForm key={stock.variantId} stock={stock} />
							))}
					</DetailSection>
					<DetailSection title={t("variant.facts")} help="stock:facts">
						<DetailFacts
							facts={[
								{ label: t("variant.sku"), value: <span className="font-mono">{stock.sku}</span> },
								{
									label: t("variant.ean"),
									value: stock.ean ? (
										<span className="font-mono">{stock.ean}</span>
									) : (
										<span className="text-muted-foreground">{t("variant.noEan")}</span>
									),
								},
								{ label: t("variant.active"), value: stock.active ? t("variant.yes") : t("variant.no") },
							]}
						/>
					</DetailSection>
				</>
			}
		>
			<DetailSection
				title={t("variant.levels")}
				description={t("variant.levelsDescription")}
				help="stock:levels"
				actions={<StockLevelBadge stock={stock} />}
			>
				<LevelsTable stock={stock} />
			</DetailSection>
			<DetailSection title={t("history.title")} description={t("history.description")} help="stock:history">
				<MovementHistory page={history} search={search} />
			</DetailSection>
		</DetailLayout>
	);
}
